// components/LogoNavBar.js
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../styles/LogoNavBar.css';
import { useAppContext } from '../context/AppContext';

const LogoNavBar = ({ disabled, className = 'navbar' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token, logout } = useAppContext();


  // Mobile drawer state
  const [menuOpen, setMenuOpen] = React.useState(false);
  const toggleMenu = () => setMenuOpen(v => !v);
  const closeMenu = () => setMenuOpen(false);

  const isActive = (path) => location.pathname === path;
  const isAuthenticated = !!token && !!user;
  const navDisabled = !!disabled;

  return (
    <nav className={className}>
      {/* Dim overlay when drawer is open */}
      {menuOpen && <div className="mobile-overlay" onClick={closeMenu} />}

      {/* Left cluster */}
      <div className="navbar-left">
        {/* Hamburger (mobile only) */}
        <button className="hamburger-btn" onClick={toggleMenu} aria-label="Open menu">
          ☰
        </button>

        {/* Logo — left on desktop; centered on mobile via CSS */}
        <div
          className="logo"
          onClick={() => navigate('/')}
          role="button"
          aria-label="Home"
          tabIndex={0}
        >
          Cl<span className="highlight-ai">ai</span>ri<span className="highlight-fi">fi</span>
        </div>

        {/* Desktop primary links */}
        <ul className="nav-links">
          <li className={isActive('/dashboard') ? 'active' : ''}>
            {navDisabled ? (
              <span className="disabled" aria-disabled="true" tabIndex={-1}>Dashboard</span>
            ) : (
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
            )}
          </li>

          <li className={isActive('/forecast') ? 'active' : ''}>
            {navDisabled ? (
              <span className="disabled" aria-disabled="true" tabIndex={-1}>Forecast</span>
            ) : (
              <Link to="/forecast" className="nav-link">Forecast</Link>
            )}
          </li>

          <li className={isActive('/strategy') ? 'active' : ''}>
            {navDisabled ? (
              <span className="disabled" aria-disabled="true" tabIndex={-1}>Strategy</span>
            ) : (
              <Link to="/strategy" className="nav-link">Strategy</Link>
            )}
          </li>

          <li className={isActive('/cfo-tools') ? 'active' : ''}>
            {navDisabled ? (
              <span className="disabled" aria-disabled="true" tabIndex={-1}>CFO Tools</span>
            ) : (
              <Link to="/cfo-tools" className="nav-link">CFO Tools</Link>
            )}
          </li>
        </ul>
      </div>

      {/* Right cluster */}
      <div className="navbar-right">
        {/* Desktop Upload dropdown */}
        <div className="desktop-only account-dropdown">
          <div className="account-label">Upload</div>
          <div className="dropdown-menu">
            <Link to="/upload">Upload financial report</Link>
            <Link to="/transactions-upload">Upload transaction statement</Link>
          </div>
        </div>

        {/* Desktop Account dropdown */}
        <div className="desktop-only account-dropdown">
          <div className="account-label">My Account</div>
          <div className="dropdown-menu">
            {isAuthenticated ? (
              <>
                <Link to="/account">Account</Link>
                <button className="logout-button" onClick={logout}>Sign Out</button>
              </>

            ) : (
              <>
                <Link to="/login">Login</Link>
                <Link to="/register">Create account</Link>
              </>
            )}
          </div>
        </div>

        {/* Feedback button (always visible on desktop) */}
        <div className="desktop-only">
          <Link to="/feedback" className="feedback-btn">Feedback</Link>
        </div>


        {/* Mobile Account icon (emoji) */}
        <button
          className="mobile-account-btn"
          onClick={() => navigate('/account')}
          aria-label="Account"
          title="Account"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="50"
            height="50"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 21a8 8 0 0 0-16 0" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </button>
      </div>

      {/* Mobile slide-out drawer */}
      <div className={`mobile-drawer ${menuOpen ? 'open' : ''}`} role="dialog" aria-modal="true">
        <div className="drawer-header">
          <div className="drawer-logo" onClick={() => { navigate('/'); closeMenu(); }}>
            Cl<span className="highlight-ai">ai</span>ri<span className="highlight-fi">fi</span>
          </div>
          <button className="drawer-close" onClick={closeMenu} aria-label="Close menu">×</button>
        </div>

        <ul className="mobile-menu">
          <li><Link to="/dashboard" onClick={closeMenu}>Dashboard</Link></li>
          <li><Link to="/forecast" onClick={closeMenu}>Forecast</Link></li>
          <li><Link to="/strategy" onClick={closeMenu}>Strategy</Link></li>
          <li><Link to="/cfo-tools" onClick={closeMenu}>CFO Tools</Link></li>

          <li>
            <Link to="/upload" onClick={closeMenu}>
              Upload
            </Link>
          </li>
          <li><Link to="/feedback" onClick={closeMenu}>Feedback</Link></li>



          {isAuthenticated ? (
            <>
              <li><Link to="/account" onClick={closeMenu}>Account</Link></li>
              <li>
                <button
                  className="signout-btn"
                  onClick={() => { logout(); closeMenu(); }}
                >
                  Sign Out
                </button>
              </li>
            </>
          ) : (
            <>
              <li><Link to="/login" onClick={closeMenu}>Login</Link></li>
              <li><Link to="/register" onClick={closeMenu}>Create account</Link></li>
            </>
          )}
        </ul>
      </div>
    </nav>
  );
};

export default LogoNavBar;
